import React, { useEffect, useState, useCallback } from "react";
import Swal from 'sweetalert2';
import adminAxios from "../admin/adminAxios"; // Isolated tenant-aware client
import { useTheme } from "../context/ThemeContext"; 
import { useAuth } from "../context/AuthContext";
import UserTopbar from "../components/UserTopbar"; 

function Home() {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null); 
  const [banners, setBanners] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  const [wishlist, setWishlist] = useState({});
  const [searchQuery, setSearchQuery] = useState("");

  const { colors } = useTheme();
  const { user } = useAuth();

  // Retrieve isolated tenant context identifier matching admin side logic
  const tenantId = user?.adminId || localStorage.getItem("adminId") || localStorage.getItem("tenantId") || "global";

  const activeBanners = banners.filter(b => b.path && b.path.trim() !== "");

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchBanners = useCallback(async () => {
    try {
      const { data } = await adminAxios.get(`/banners`, { params: { tenantId } });
      setBanners(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("[System] Banner sync failure:", err);
    }
  }, [tenantId]);

  const fetchProducts = useCallback(async () => {
    try {
      const { data } = await adminAxios.get(`/products`, { params: { tenantId } });
      const list = Array.isArray(data) ? data : [];
      setProducts(list);
      setFilteredProducts(list);
    } catch (err) {
      console.error("[System] Inventory sync failure:", err);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchBanners();
    fetchProducts();
  }, [fetchBanners, fetchProducts]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter(p => 
        p.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredProducts(filtered);
    }
  }, [searchQuery, products]);

  const getImageUrl = (filePath) => {
    if (!filePath) return "/placeholder.png";
    if (filePath.startsWith("http") || filePath.startsWith("blob:")) return filePath;
    const cleanPath = filePath
        .replace(/^(\/|\\)?uploads(\/|\\)/i, "")
        .replace(/^(\/|\\)?maritime_uplink_assets(\/|\\)/i, "")
        .replace(/^\//, "");
    const API_URL_RAW = process.env.REACT_APP_API_URL || "https://maritime-supply-suite-backend.onrender.com"; 
    const API_BASE = API_URL_RAW.replace(/\/api\/?$/, "");
    return `${API_BASE}/uploads/${cleanPath}`;
  };

  const isVideo = (path) => {
    if (!path) return false;
    return path.match(/\.(mp4|webm|ogg)$/) !== null;
  };

  const toggleWishlist = (e, id) => {
    e.stopPropagation();
    setWishlist(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleCategoryClick = () => {
    Swal.fire({
      text: 'Synchronizing category data...',
      position: 'top',
      showConfirmButton: false,
      timer: 2000,
      toast: true,
      background: colors.brand,
      color: '#ffffff'
    });
  };

  const scrollGallery = (id, direction) => {
    const container = document.getElementById(id);
    if (container) {
      const scrollAmount = container.offsetWidth;
      container.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    }
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating || 5);
    for (let i = 1; i <= 5; i++) {
      stars.push(<span key={i} style={{ color: i <= fullStars ? colors.star : colors.muted, fontSize: '11px' }}>★</span>);
    }
    return stars;
  };

  const handleAddToCart = async (product) => {
    try {
      const res = await adminAxios.post("/orders/buy", {
        productId: product._id,
        tenantId: tenantId !== "global" ? tenantId : undefined
      });

      if (res.status === 200 || res.status === 201) {
        Swal.fire({
          title: 'Success',
          text: 'Asset successfully added to your inventory.',
          icon: 'success',
          confirmButtonColor: colors.brand
        });
        fetchProducts();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.response?.data?.msg || "Unable to synchronize transaction.";
      Swal.fire({
        title: 'Clearance Failed',
        text: errorMsg,
        icon: 'error',
        confirmButtonColor: colors.brand
      });
    }
  };

  const layoutStyle = { 
    backgroundColor: colors.bg, 
    minHeight: "100vh", 
    paddingTop: "180px", 
    paddingBottom: "100px", 
    fontFamily: "'Inter', sans-serif", 
    color: colors.text, 
    transition: '0.3s ease' 
  };
  
  const cardStyle = { backgroundColor: colors.card, borderRadius: "18px", overflow: "hidden", border: `1px solid ${colors.border}`, transition: "0.3s cubic-bezier(0.4, 0, 0.2, 1)" };
  const searchInputStyle = { width: "100%", padding: "12px 20px", borderRadius: "10px", border: `1px solid ${colors.border}`, backgroundColor: colors.card, color: colors.text, outline: "none" };

  return (
    <div style={layoutStyle} data-tenant-scope={tenantId}>
      <UserTopbar title="" />

      <div style={contentContainer}>
        <div style={searchWrapper}>
          <input 
            type="text" 
            placeholder="Search manifest..." 
            style={searchInputStyle} 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {activeBanners.length > 0 && (
          <div style={{...carouselWrapper, borderColor: colors.border}}>
            <div style={bannerTrackAnim}>
              {[...activeBanners, ...activeBanners].map((banner, idx) => (
                <div key={`banner-${idx}`} style={bannerSlide}>
                  <div style={{...bannerCard, backgroundColor: colors.brand}}>
                    {isVideo(banner.path) ? (
                      <video src={getImageUrl(banner.path)} style={{...bannerImgStyle, height: isMobile ? "120px" : "180px"}} autoPlay muted loop playsInline />
                    ) : (
                      <img src={getImageUrl(banner.path)} alt="" style={{...bannerImgStyle, height: isMobile ? "120px" : "180px"}} />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={categorySection}>
          <div style={scrollWrapper}>
            <div style={scrollTrack} className="scroll-infinite">
              {[...CATEGORIES, ...CATEGORIES].map((cat, index) => (
                <div key={`cat-${index}`} style={categoryItem} onClick={handleCategoryClick}>
                    <div style={{...categoryCircle, backgroundColor: colors.card, borderColor: colors.border}}>
                      <img src={cat.img} alt={cat.name} style={categoryImg} />
                    </div>
                    <span style={{...categoryText, color: colors.muted}}>{cat.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <div style={{...statusMsg, color: colors.muted}}>Retrieving manifest...</div>
        ) : (
          <div style={{ ...gridStyle, gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(auto-fill, minmax(260px, 1fr))", gap: isMobile ? "12px" : "24px" }}>
            {filteredProducts.map((p) => {
              const imageGallery = (Array.isArray(p.images) && p.images.length > 0) ? p.images : [p.image || "/placeholder.png"];
              const margin = Number(p.commissionYield || p.profit || 0);

              return (
                <div key={p._id} style={cardStyle} className="product-card">
                  <div style={{ ...galleryWrapper, height: isMobile ? "140px" : "190px" }}>
                    <button onClick={(e) => toggleWishlist(e, p._id)} style={{...wishlistBtn, color: colors.brand}}>{wishlist[p._id] ? '★' : '☆'}</button>
                    
                    {imageGallery.length > 1 && (
                      <>
                        <button style={navArrowLeft} onClick={(e) => { e.stopPropagation(); scrollGallery(`gallery-${p._id}`, 'left'); }}>‹</button>
                        <button style={navArrowRight} onClick={(e) => { e.stopPropagation(); scrollGallery(`gallery-${p._id}`, 'right'); }}>›</button>
                      </>
                    )}

                    <div id={`gallery-${p._id}`} style={carouselContainer} className="custom-scrollbar">
                      {imageGallery.map((img, index) => (
                        <div key={`${p._id}-img-${index}`} style={carouselItem}>
                          <img src={getImageUrl(img)} alt={p.name} style={imgStyle} />
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div style={{ ...cardBody, padding: isMobile ? "12px" : "18px" }}>
                    <div style={{ ...productName, color: colors.text, fontSize: isMobile ? "13px" : "15px" }}>{p.name}</div>
                    <div style={ratingRow}>
                      <div>{renderStars(p.rating)}</div>
                      <div style={{...soldBadge, color: colors.muted}}>{p.volumeThroughput || "0"} processed</div>
                    </div>
                    <div style={priceMarginRow}>
                      <div style={{...priceStyle, color: colors.text}}>${Number(p.price || 0).toLocaleString()}</div>
                      <div style={{...marginLabel, color: colors.muted}}>Yield: <span style={{ color: colors.brand }}>+${margin.toLocaleString()}</span></div>
                    </div>
                    
                    <div style={actionRow}>
                      <button 
                        onClick={() => handleAddToCart(p)} 
                        style={{
                          ...viewBtn, 
                          background: colors.brand, 
                          color: '#ffffff', 
                          border: 'none'
                        }}
                      >
                        Add to Cart
                      </button>
                      <button 
                        onClick={() => setSelectedProduct(p)} 
                        style={{
                          ...viewBtn, 
                          color: colors.text, 
                          border: `1px solid ${colors.border}`
                        }}
                      >
                        Inspect
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {selectedProduct && (
        <div style={modalOverlay} onClick={() => setSelectedProduct(null)}>
          <div style={{...modalContent, backgroundColor: colors.card, border: `1px solid ${colors.border}`}} onClick={e => e.stopPropagation()}>
            <button style={{...closeBtn, color: colors.muted}} onClick={() => setSelectedProduct(null)}>×</button>
            <div style={modalScrollArea} className="custom-scrollbar">
              <div style={{ ...galleryWrapper, height: "240px", marginBottom: "15px", borderRadius: "12px" }}>
                  <div id={`modal-gallery-${selectedProduct._id}`} style={carouselContainer} className="custom-scrollbar">
                    {(Array.isArray(selectedProduct.images) && selectedProduct.images.length > 0 ? selectedProduct.images : [selectedProduct.image]).map((img, idx) => (
                       <div key={`modal-img-${idx}`} style={carouselItem}><img src={getImageUrl(img)} alt="" style={imgStyle} /></div>
                    ))}
                  </div>
              </div>
              <h3 style={{ margin: "5px 0", fontSize: '18px', color: colors.text }}>{selectedProduct.name}</h3>
              <div style={{...priceStyle, color: colors.brand}}>Value: ${Number(selectedProduct.price || 0).toLocaleString()}</div>
              <p style={{...modalDesc, color: colors.muted}}>{selectedProduct.specifications || selectedProduct.details || "Details pending."}</p>
              
              <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                <button 
                  style={{...viewBtn, background: colors.brand, color: '#fff', border: 'none'}} 
                  onClick={() => {
                    handleAddToCart(selectedProduct);
                    setSelectedProduct(null);
                  }}
                >
                  Add to Cart
                </button>
                <button 
                  style={{...viewBtn, color: colors.text, border: `1px solid ${colors.border}`}} 
                  onClick={() => setSelectedProduct(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes scrollInfinite { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        @keyframes bannerLoop { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .scroll-infinite { animation: scrollInfinite 45s linear infinite; }
        .product-card:hover { transform: translateY(-4px); border-color: ${colors.brand} !important; }
        .custom-scrollbar::-webkit-scrollbar { height: 2px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: ${colors.brand}; }
        .custom-scrollbar { scroll-behavior: smooth; }
      `}</style>
    </div>
  );
}

const CATEGORIES = [
  { name: "Retail Goods", img: "https://cdn-icons-png.flaticon.com/512/3759/3759601.png" },
  { name: "Technical", img: "https://cdn-icons-png.flaticon.com/512/2092/2092130.png" },
  { name: "Automotive", img: "https://cdn-icons-png.flaticon.com/512/1048/1048313.png" },
  { name: "Premium Units", img: "https://cdn-icons-png.flaticon.com/512/610/610120.png" },
  { name: "Logistics", img: "https://cdn-icons-png.flaticon.com/512/9561/9561688.png" },
  { name: "Heavy Duty", img: "https://cdn-icons-png.flaticon.com/512/2345/2345429.png" },
  { name: "Electronics", img: "https://cdn-icons-png.flaticon.com/512/12611/12611186.png" },
  { name: "Navigation", img: "https://cdn-icons-png.flaticon.com/512/149/149060.png" },
  { name: "Safety Gear", img: "https://cdn-icons-png.flaticon.com/512/1150/1150612.png" },
  { name: "Maintenance", img: "https://cdn-icons-png.flaticon.com/512/3252/3252869.png" },
  { name: "Cooling Sys", img: "https://cdn-icons-png.flaticon.com/512/2103/2103633.png" },
  { name: "Power Grid", img: "https://cdn-icons-png.flaticon.com/512/2814/2814577.png" },
  { name: "Oil & Gas", img: "https://cdn-icons-png.flaticon.com/512/2921/2921927.png" },
  { name: "Deck Assets", img: "https://cdn-icons-png.flaticon.com/512/3561/3561582.png" },
  { name: "Hardware", img: "https://cdn-icons-png.flaticon.com/512/2505/2505264.png" },
  { name: "Plumbing", img: "https://cdn-icons-png.flaticon.com/512/3094/3094833.png" },
  { name: "Hydraulics", img: "https://cdn-icons-png.flaticon.com/512/9133/9133507.png" },
  { name: "Cargo Gear", img: "https://cdn-icons-png.flaticon.com/512/2897/2897868.png" }
];

/* ===== STYLES ===== */
const contentContainer = { maxWidth: "1100px", margin: "0 auto", padding: "0 15px" };
const searchWrapper = { marginBottom: "20px" };
const carouselWrapper = { position: "relative", marginBottom: "30px", overflow: "hidden", borderRadius: "16px", border: `1px solid`, width: "100%" };
const bannerTrackAnim = { display: "flex", width: "fit-content", animation: "bannerLoop 20s linear infinite" };
const bannerSlide = { width: "calc(100vw - 30px)", maxWidth: "1070px", flexShrink: 0 }; 
const bannerImgStyle = { width: "100%", objectFit: "fill", display: "block" };
const bannerCard = { width: "100%", height: "100%" };
const categorySection = { padding: "1px 0 15px 0" };
const scrollWrapper = { overflow: "hidden", width: "100%" };
const scrollTrack = { display: "flex", width: "fit-content", gap: "25px" };
const categoryItem = { display: "flex", flexDirection: "column", alignItems: "center", cursor: "pointer", minWidth: "85px" };
const categoryCircle = { width: "55px", height: "55px", borderRadius: "14px", display: "flex", alignItems: "center", justifyContent: "center", border: `1px solid`, marginBottom: "8px" };
const categoryImg = { width: "60%", height: "60%", objectFit: "contain" };
const categoryText = { fontSize: "10px", fontWeight: "700", textAlign: "center", textTransform: "uppercase" };
const gridStyle = { display: "grid" };
const galleryWrapper = { position: "relative", overflow: "hidden", backgroundColor: "#000" };
const wishlistBtn = { position: 'absolute', top: '12px', right: '12px', zIndex: 10, background: 'rgba(15, 23, 42, 0.85)', border: 'none', borderRadius: '10px', width: '32px', height: '32px', cursor: 'pointer' };
const navArrowBase = { position: 'absolute', top: '50%', transform: 'translateY(-50%)', zIndex: 5, background: 'rgba(10, 17, 32, 0.7)', border: 'none', borderRadius: '50%', width: '28px', height: '28px', color: "#fff", cursor: 'pointer' };
const navArrowLeft = { ...navArrowBase, left: '10px' };
const navArrowRight = { ...navArrowBase, right: '10px' };
const carouselContainer = { display: "flex", overflowX: "hidden", width: "100%", height: "100%" };
const carouselItem = { flex: "0 0 100%", height: "100%" };
const imgStyle = { width: "100%", height: "100%", objectFit: "fill" };
const cardBody = { display: "flex", flexDirection: "column" };
const productName = { fontWeight: "700", marginBottom: "6px" };
const ratingRow = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" };
const soldBadge = { fontSize: "10px", fontWeight: "800" };
const priceMarginRow = { display: "flex", justifyContent: "space-between", marginBottom: "18px" };
const priceStyle = { fontWeight: "800", fontSize: "19px" };
const marginLabel = { fontSize: "11px" };
const actionRow = { display: "flex", gap: "10px" };
const viewBtn = { flex: 1, background: "rgba(128,128,128,0.05)", padding: "10px", borderRadius: "8px", fontWeight: "700", cursor: "pointer" };
const modalOverlay = { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(2, 6, 23, 0.92)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 2000 };
const modalContent = { borderRadius: "28px", maxWidth: "440px", width: "100%", padding: "28px", position: 'relative' };
const modalScrollArea = { maxHeight: "78vh", overflowY: "auto" };
const closeBtn = { position: "absolute", top: "16px", right: "20px", background: "none", border: "none", fontSize: "24px", cursor: 'pointer' };
const modalDesc = { fontSize: "14px", margin: "18px 0" };
const statusMsg = { textAlign: "center", padding: "120px" };

export default Home;